// Copyright (c) 2026 TRIVONT IT Private Limited. All rights reserved.

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  // Only add the regular user token if no Authorization header is already set
  // (prevents overwriting superadmin or other custom auth headers)
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('tenant_id');
  if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const req = error.config;
    // Don't interfere with superadmin endpoints — they handle auth themselves
    if (req.url && req.url.includes('/superadmin/')) {
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !req._retry) {
      req._retry = true;
      const refresh = localStorage.getItem('refresh_token');
      if (refresh) {
        try {
          const res = await axios.post(`${API_URL}/api/auth/refresh/`, { refresh });
          localStorage.setItem('access_token', res.data.access);
          req.headers.Authorization = `Bearer ${res.data.access}`;
          return api(req);
        } catch { localStorage.clear(); window.location.href = '/login'; }
      }
    }
    return Promise.reject(error);
  }
);

// === AUTH ===
export const authAPI = {
  login: (data) => api.post('/auth/login/', data),
  register: (data) => api.post('/auth/register/', data),
  me: () => api.get('/auth/me/'),
  logout: (refresh) => api.post('/auth/logout/', { refresh }),
  changePassword: (data) => api.post('/auth/change-password/', data),
  forgotPassword: (email) => api.post('/auth/forgot-password/', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password/', { token, new_password }),
  googleAuth: (token) => api.post('/auth/oauth/google/', { token }),
  azureAuth: (code, redirectUri) => api.post('/auth/oauth/azure-ad/', { code, redirect_uri: redirectUri }),
};

// === MFA ===
export const mfaAPI = {
  verify: (data) => api.post('/auth/mfa/verify/', data),
  sendEmailOtp: (data) => api.post('/auth/mfa/send-email-otp/', data),
  setupTotp: () => api.post('/auth/mfa/setup-totp/'),
  confirmTotp: (otp) => api.post('/auth/mfa/confirm-totp/', { otp }),
  confirmEmail: (otp) => api.post('/auth/mfa/confirm-email/', { otp }),
  disable: (password) => api.post('/auth/mfa/disable/', { password }),
  status: () => api.get('/auth/mfa/status/'),
  firstLoginSetup: (data) => api.post('/auth/mfa/first-login-setup/', data),
};

// === TICKETS ===
export const incidentAPI = {
  list: (params) => api.get('/tickets/incidents/', { params }),
  get: (id) => api.get(`/tickets/incidents/${id}/`),
  create: (data) => api.post('/tickets/incidents/', data),
  update: (id, data) => api.put(`/tickets/incidents/${id}/`, data),
  patch: (id, data) => api.patch(`/tickets/incidents/${id}/`, data),
  updatePriority: (id, data) => api.patch(`/tickets/incidents/${id}/update_priority/`, data),
  updateStatus: (id, status, extra = {}) => api.patch(`/tickets/incidents/${id}/update_status/`, { status, ...extra }),
  assign: (id, data) => api.post(`/tickets/incidents/${id}/assign/`, data),
  addComment: (id, data) => api.post(`/tickets/incidents/${id}/add_comment/`, data),
  getComments: (id) => api.get(`/tickets/incidents/${id}/comments/`),
  markCommentsRead: (id) => api.post(`/tickets/incidents/${id}/mark_comments_read/`),
  getHistory: (id) => api.get(`/tickets/incidents/${id}/history/`),
  getAttachments: (id) => api.get(`/tickets/attachments/?ticket_type=incident&ticket_id=${id}`),
  getHistory: (id) => api.get(`/tickets/incidents/${id}/history/`),
  bulkAction: (data) => api.post('/tickets/incidents/bulk_action/', data),
  pauseSla: (id) => api.post(`/tickets/incidents/${id}/pause_sla/`),
  resumeSla: (id) => api.post(`/tickets/incidents/${id}/resume_sla/`),
};

export const serviceRequestAPI = {
  list: (params) => api.get('/tickets/service-requests/', { params }),
  get: (id) => api.get(`/tickets/service-requests/${id}/`),
  create: (data) => api.post('/tickets/service-requests/', data),
  update: (id, data) => api.put(`/tickets/service-requests/${id}/`, data),
  patch: (id, data) => api.patch(`/tickets/service-requests/${id}/`, data),
  updatePriority: (id, data) => api.patch(`/tickets/service-requests/${id}/update_priority/`, data),
  updateStatus: (id, status, extra = {}) => api.patch(`/tickets/service-requests/${id}/update_status/`, { status, ...extra }),
  assign: (id, data) => api.post(`/tickets/service-requests/${id}/assign/`, data),
  submit: (id) => api.post(`/tickets/service-requests/${id}/submit/`),
  addComment: (id, data) => api.post(`/tickets/service-requests/${id}/add_comment/`, data),
  getComments: (id) => api.get(`/tickets/service-requests/${id}/comments/`),
  markCommentsRead: (id) => api.post(`/tickets/service-requests/${id}/mark_comments_read/`),
  getHistory: (id) => api.get(`/tickets/service-requests/${id}/history/`),
  getAttachments: (id) => api.get(`/tickets/attachments/?ticket_type=sr&ticket_id=${id}`),
};

export const changeRequestAPI = {
  list: (params) => api.get('/tickets/change-requests/', { params }),
  get: (id) => api.get(`/tickets/change-requests/${id}/`),
  create: (data) => api.post('/tickets/change-requests/', data),
  update: (id, data) => api.put(`/tickets/change-requests/${id}/`, data),
  patch: (id, data) => api.patch(`/tickets/change-requests/${id}/`, data),
  updatePriority: (id, data) => api.patch(`/tickets/change-requests/${id}/update_priority/`, data),
  updateStatus: (id, status, extra = {}) => api.patch(`/tickets/change-requests/${id}/update_status/`, { status, ...extra }),
  assign: (id, data) => api.post(`/tickets/change-requests/${id}/assign/`, data),
  submit: (id) => api.post(`/tickets/change-requests/${id}/submit/`),
  addComment: (id, data) => api.post(`/tickets/change-requests/${id}/add_comment/`, data),
  getComments: (id) => api.get(`/tickets/change-requests/${id}/comments/`),
  markCommentsRead: (id) => api.post(`/tickets/change-requests/${id}/mark_comments_read/`),
  getHistory: (id) => api.get(`/tickets/change-requests/${id}/history/`),
  getAttachments: (id) => api.get(`/tickets/attachments/?ticket_type=cr&ticket_id=${id}`),
  schedule: (id, data) => api.post(`/tickets/change-requests/${id}/schedule/`, data),
};

// === CATEGORIES & APPS ===
export const categoryAPI = {
  list: (params) => api.get('/tickets/categories/', { params: { view: 'tree', ...params } }),
  listFlat: (params) => api.get('/tickets/categories/', { params: { view: 'flat', ...params } }),
  create: (data) => api.post('/tickets/categories/', data),
  update: (id, data) => api.put(`/tickets/categories/${id}/`, data),
  delete: (id) => api.delete(`/tickets/categories/${id}/`),
};

export const applicationAPI = {
  list: (params) => api.get('/tickets/applications/', { params }),
  get: (id) => api.get(`/tickets/applications/${id}/`),
  create: (data) => api.post('/tickets/applications/', data),
  update: (id, data) => api.put(`/tickets/applications/${id}/`, data),
  delete: (id) => api.delete(`/tickets/applications/${id}/`),
};

// === SLA ===
export const slaAPI = {
  listPolicies: () => api.get('/sla/policies/'),
  createPolicy: (data) => api.post('/sla/policies/', data),
  updatePolicy: (id, data) => api.put(`/sla/policies/${id}/`, data),
  deletePolicy: (id) => api.delete(`/sla/policies/${id}/`),
  listBusinessHours: () => api.get('/sla/business-hours/'),
  updateBusinessHours: (id, data) => api.put(`/sla/business-hours/${id}/`, data),
  listHolidays: () => api.get('/sla/holidays/'),
  createHoliday: (data) => api.post('/sla/holidays/', data),
  deleteHoliday: (id) => api.delete(`/sla/holidays/${id}/`),
  // SLA global config (business hours mode toggle)
  getSLAConfig: () => api.get('/sla/config/'),
  updateSLAConfig: (data) => api.patch('/sla/config/', data),
  listEscalationPolicies: () => api.get('/sla/escalation-policies/'),
  createEscalationPolicy: (data) => api.post('/sla/escalation-policies/', data),
  updateEscalationPolicy: (id, data) => api.put(`/sla/escalation-policies/${id}/`, data),
  deleteEscalationPolicy: (id) => api.delete(`/sla/escalation-policies/${id}/`),
  // SLA Policy Sets (application-specific)
  listPolicySets: () => api.get('/sla/policy-sets/'),
  createPolicySet: (data) => api.post('/sla/policy-sets/', data),
  updatePolicySet: (id, data) => api.put(`/sla/policy-sets/${id}/`, data),
  deletePolicySet: (id) => api.delete(`/sla/policy-sets/${id}/`),
};

// === APPROVALS ===
export const approvalAPI = {
  listConfigs: () => api.get('/approvals/configs/'),
  createConfig: (data) => api.post('/approvals/configs/', data),
  updateConfig: (id, data) => api.put(`/approvals/configs/${id}/`, data),
  listPending: () => api.get('/approvals/pending/'),
  approve: (id, comments) => api.post(`/approvals/${id}/approve/`, { comments }),
  reject: (id, comments) => api.post(`/approvals/${id}/reject/`, { comments }),
  listDelegations: () => api.get('/approvals/delegations/'),
  createDelegation: (data) => api.post('/approvals/delegations/', data),
  updateDelegation: (id, data) => api.patch(`/approvals/delegations/${id}/`, data),
  deleteDelegation: (id) => api.delete(`/approvals/delegations/${id}/`),
  deactivateDelegation: (id) => api.post(`/approvals/delegations/${id}/deactivate/`),
  activateDelegation: (id) => api.post(`/approvals/delegations/${id}/activate/`),
};

// === TEAMS ===
export const teamAPI = {
  list: () => api.get('/teams/teams/'),
  get: (id) => api.get(`/teams/teams/${id}/`),
  create: (data) => api.post('/teams/teams/', data),
  update: (id, data) => api.put(`/teams/teams/${id}/`, data),
  patch: (id, data) => api.patch(`/teams/teams/${id}/`, data),
  addMember: (teamId, data) => api.post(`/teams/teams/${teamId}/add_member/`, data),
  removeMember: (teamId, userId) => api.delete(`/teams/teams/${teamId}/remove_member/`, { data: { user_id: userId } }),
  listShifts: () => api.get('/teams/shifts/'),
  createShift: (data) => api.post('/teams/shifts/', data),
  updateShift: (id, data) => api.put(`/teams/shifts/${id}/`, data),
  deleteShift: (id) => api.delete(`/teams/shifts/${id}/`),
  listShiftAssignments: (params) => api.get('/teams/shift-assignments/', { params }),
  createShiftAssignment: (data) => api.post('/teams/shift-assignments/', data),
  deleteShiftAssignment: (id) => api.delete(`/teams/shift-assignments/${id}/`),
  deleteShiftByUserDate: (userId, date) => api.post('/teams/shift-assignments/remove_by_date/', { user: userId, date }),
  listEscalationRules: () => api.get('/teams/escalation-rules/'),
  createEscalationRule: (data) => api.post('/teams/escalation-rules/', data),
  updateEscalationRule: (id, data) => api.put(`/teams/escalation-rules/${id}/`, data),
};

// === USERS ===
export const userAPI = {
  list: (params) => api.get('/auth/users/', { params }),
  get: (id) => api.get(`/auth/users/${id}/`),
  create: (data) => api.post('/auth/users/', data),
  update: (id, data) => api.put(`/auth/users/${id}/`, data),
  resetPassword: (id, new_password) => api.post(`/auth/users/${id}/reset_password/`, { new_password }),
  listRoles: () => api.get('/auth/roles/'),
  listDepartments: () => api.get('/auth/departments/'),
  createDepartment: (data) => api.post('/auth/departments/', data),
  updateDepartment: (id, data) => api.put(`/auth/departments/${id}/`, data),
  deleteDepartment: (id) => api.delete(`/auth/departments/${id}/`),
};

// === REPORTS ===
export const reportAPI = {
  dashboard: (params = {}) => api.get('/reports/dashboard/', { params }),
  slaBreach: (params) => api.get('/reports/sla-breach/', { params }),
  slaCompliance: (params) => api.get('/reports/sla-compliance/', { params }),
  ticketVolume: (params) => api.get('/reports/ticket-volume/', { params }),
  agentPerformance: (params) => api.get('/reports/agent-performance/', { params }),
};

// === CMDB ===
export const cmdbAPI = {
  listAssets: (params) => api.get('/cmdb/assets/', { params }),
  getAsset: (id) => api.get(`/cmdb/assets/${id}/`),
  createAsset: (data) => api.post('/cmdb/assets/', data),
  updateAsset: (id, data) => api.put(`/cmdb/assets/${id}/`, data),
  deleteAsset: (id) => api.delete(`/cmdb/assets/${id}/`),
};

// === KNOWLEDGE BASE ===
export const kbAPI = {
  listCategories: () => api.get('/kb/categories/'),
  createCategory: (data) => api.post('/kb/categories/', data),
  ticketCategories: () => api.get('/kb/articles/ticket_categories/'),
  listArticles: (params) => api.get('/kb/articles/', { params }),
  getArticle: (id) => api.get(`/kb/articles/${id}/`),
  createArticle: (data) => api.post('/kb/articles/', data),
  updateArticle: (id, data) => api.put(`/kb/articles/${id}/`, data),
  deleteArticle: (id) => api.delete(`/kb/articles/${id}/`),
  voteHelpful: (id, vote) => api.post(`/kb/articles/${id}/helpful/`, { vote }),
  recordView: (id) => api.post(`/kb/articles/${id}/view/`),
};

// === AUTOMATION ===
export const automationAPI = {
  listRules: () => api.get('/automation/rules/'),
  getRule: (id) => api.get(`/automation/rules/${id}/`),
  createRule: (data) => api.post('/automation/rules/', data),
  updateRule: (id, data) => api.put(`/automation/rules/${id}/`, data),
  deleteRule: (id) => api.delete(`/automation/rules/${id}/`),
};

// === TENANT SETTINGS ===
export const tenantAPI = {
  getSettings: () => api.get('/tenants/settings/'),
  updateSettings: (data) => api.patch('/tenants/settings/', data),
  uploadLogo: (file, appName) => {
    const fd = new FormData();
    fd.append('logo', file);
    if (appName) fd.append('app_name', appName);
    return api.patch('/tenants/settings/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeLogo: () => api.patch('/tenants/settings/', { remove_logo: 'true' }),
};

// === AUDIT LOGS ===
export const auditAPI = {
  list: (params) => api.get('/tickets/audit-logs/', { params }),
};

// === NOTIFICATIONS (in-app) ===
export const notificationAPI = {
  list: () => api.get('/notifications/'),
  markRead: (id) => api.post(`/notifications/${id}/read/`),
  markAllRead: () => api.post('/notifications/read_all/'),
  unreadCount: () => api.get('/notifications/unread_count/'),
};

// === CMDB RELATIONSHIPS ===
export const cmdbRelationshipAPI = {
  list: (params) => api.get('/cmdb/relationships/', { params }),
  create: (data) => api.post('/cmdb/relationships/', data),
  delete: (id) => api.delete(`/cmdb/relationships/${id}/`),
};

// === FILE UPLOAD (multipart) ===
export const uploadAttachment = (ticketType, ticketId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('ticket_type', ticketType);
  formData.append('ticket_id', ticketId);
  formData.append('file_name', file.name);
  formData.append('file_size', file.size);
  return api.post('/tickets/attachments/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export default api;
